<template>
  <div
    v-if="store.state.site"
    :class="$route.meta.fillHeight ? 'fill-height' : ''"
  >
    <v-app :class="vAppClass">
      <template v-if="store.state.pages.Welcome.alert && $route.name === 'Welcome'">
        <v-sheet role="alert">
          <div
            class="bg-primary c-white alert-bar d-flex align-center pe-2 w-100pc text-center wrapper"
          >
            <a
              class="skip-link"
              href="#main"
            >
              {{ $bct('Skip to main content') }}
            </a>
            <v-icon
              class="d-inline c-white pe-2"
              icon="info"
            />
            <div class="alert-wrapper">
              <MarkdownContent
                :content="store.state.pages.Welcome.alert"
                class="alert-css"
              />
            </div>
          </div>
        </v-sheet>
      </template>

      <template
        v-if="
          store.state.pages.Navigation.StructuredAlert &&
          store.state.pages.Navigation.StructuredAlert.length > 0 &&
          alertIsVisible
        "
      >
        <v-sheet role="alert">
          <div class="bg-primary c-white alert-bar structured-alert-bar d-flex">
            <v-icon
              class="d-inline c-white me-2 structured-alert-icon"
              icon="info"
            />
            <StructuredText
              :text="store.state.pages.Navigation.StructuredAlert"
              class="structured-alert-text text-center"
            />
            <v-btn
              @click="alertIsVisible = false"
              :ripple="false"
              aria-label="Close"
              class="structured-alert-close"
              icon="close"
              size="x-small"
              variant="plain"
            />
          </div>
        </v-sheet>
      </template>

      <template v-if="$vuetify.display.mdAndUp">
        <v-app-bar
          :class="$route.meta.expandedNav ? '' : 'bb-1 bc-outlined-gray'"
          class="bg-white"
          data-testid="app-bar"
          order="2"
          style="position: static"
          flat
        >
          <v-row class="d-flex align-center justify-space-between px-4">
            <v-col
              class="d-flex align-center justify-start"
              cols="12"
              lg="8"
              md="8"
            >
              <a
                class="skip-link"
                href="#main"
              >
                {{ $bct('Skip to main content') }}
              </a>
              <img
                @click="navigateToHome"
                @keyup.enter="navigateToHome"
                id="app_bar_logo"
                :alt="`${$bct('Homepage for')} ${store.state.brand.name}`"
                :src="store.state.brand.logo.full"
                class="pointer mxh-40 mxw-100pc me-8"
                role="link"
                tabindex="0"
                tracked
              />
              <!-- eslint-enable vue-router-rules/await-router-navigation -->
              <template v-if="store.state.profile">
                <div
                  v-if="$route.name == 'SearchShow'"
                  id="global-search"
                  class="flex-grow-1"
                />
                <v-btn
                  v-else-if="$route.name == 'Welcome' && store.state.profile.search_id"
                  :to="{
                    name: 'SearchShow',
                    params: { searchId: store.state.profile.search_id },
                  }"
                  class="focus-visible me-3"
                  prepend-icon="west"
                  variant="text"
                >
                  {{ $bct('Back to search') }}
                </v-btn>
              </template>
            </v-col>

            <v-col
              class="ta-right va-middle d-flex align-center justify-end"
              cols="12"
              lg="4"
              md="4"
            >
              <template v-if="store.state.config.enable_search">
                <GlobalQuery />
              </template>

              <LocaleSelector />

              <AccountMenu
                @open-complaint-form="openComplaintForm"
                @sign-out="signOut"
              />
            </v-col>
          </v-row>
        </v-app-bar>
      </template>

      <template v-else>
        <v-app-bar
          :height="
            $route.name === 'SearchShow' ? (store.state.expandAppBarSmAndDown ? 225 : 124) : '64'
          "
          class="bg-white"
          data-testid="app-bar"
          elevation="0"
          order="3"
          style="position: static"
        >
          <v-row
            class="d-flex align-center"
            dense
          >
            <v-col
              class="d-flex align-center justify-start"
              cols="3"
            >
              <a
                class="skip-link"
                href="#main"
              >
                {{ $bct('Skip to main content') }}
              </a>
              <img
                @click="navigateToHome"
                :alt="`${$bct('Homepage for')} ${store.state.brand.name}`"
                :src="store.state.brand.logo.full"
                class="pointer mxh-40 mxw-100pc"
              />
              <!-- eslint-enable vue-router-rules/await-router-navigation -->
            </v-col>

            <v-col
              class="d-flex align-center justify-end"
              cols="9"
            >
              <template v-if="store.state.config.enable_search">
                <div class="d-inline-flex">
                  <GlobalQuery />
                </div>
              </template>

              <LocaleSelector />

              <AccountMenu
                @open-complaint-form="openComplaintForm"
                @sign-out="signOut"
              />
            </v-col>

            <template v-if="$route.name == 'SearchShow'">
              <v-col cols="12">
                <div
                  id="global-search"
                  class="flex-grow-1"
                />
              </v-col>
            </template>
          </v-row>
        </v-app-bar>
      </template>

      <SnackBar
        v-model="chime"
        :centered="true"
        :text="chimeText"
        :timeout="chimeTime"
        close-on-content-click
      />

      <ConfirmDialog ref="confirmDialog" />
      <ComplaintFormDialog ref="complaintFormDialog" />

      <HelpFlyout
        ref="helpFlyoutRef"
        :show-help-button="showHelpButton"
      />

      <v-main
        id="main"
        :style="{ '--v-layout-top': '0px' }"
        class="layout--content fill-height"
      >
        <!-- eslint-disable vue-router-rules/await-router-navigation -->
        <router-view
          @chime="regularChime"
          @error="error"
          @replace="$router.push($event)"
          v-slot="{ Component }"
        >
          <!-- eslint-enable vue-router-rules/await-router-navigation -->
          <transition
            :key="route.path"
            :name="transitionName"
            mode="out-in"
          >
            <component :is="Component" />
          </transition>
        </router-view>
      </v-main>

      <!-- <UserSessionTimeout /> -->
    </v-app>
  </div>
</template>

<script setup>
import { provide } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import AccountMenu from '@/parent/components/AccountMenu.vue';
import { AMPLITUDE_ENGAGEMENT_PROVIDE_KEY } from '@/shared/composables/analytics/useAmplitudeEngagement';
import ComplaintFormDialog from '@/parent/components/form/ComplaintFormDialog.vue';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import GlobalQuery from '@/shared/components/GlobalQuery.vue';
import HelpFlyout from '@/shared/components/HelpFlyout.vue';
// import UserSessionTimeout from '@/shared/components/UserSessionTimeout.vue';
import LocaleSelector from '@/shared/components/LocaleSelector.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import { PROVIDE_KEYS } from '@/shared/assets/constants';
import { SEGMENT_PROVIDE_KEY } from '@/shared/composables/analytics/useSegment';
import SegmentService from '@/shared/services/analytics/SegmentService';
import SnackBar from '@/shared/components/SnackBar.vue';
import StructuredText from '@/shared/components/content/StructuredText.vue';
import useAmplitudeEngagementInit from '@/shared/composables/analytics/useAmplitudeEngagementInit';
import useApi from '@/shared/composables/useApi';
import useChime from '@/shared/composables/useChime';
import useLanguageHelper from '@/shared/composables/useLanguageHelper';
import useSegmentPageViews from '@/shared/composables/analytics/useSegmentPageViews';
import useTranslator from '@/shared/composables/useTranslator';

const { api } = useApi();

provide(PROVIDE_KEYS.OPEN_HELP_FLYOUT, (document) => {
  helpFlyoutRef.value?.openFlyout(document);
});
const { locale } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { $bct } = useTranslator();

// Setup translator provide/inject
provide(PROVIDE_KEYS.TRANSLATOR, $bct);

// Setup Amplitude Engagement (must be initialized before SegmentService)
const amplitudeApiKey = window.VUE_AMPLITUDE_API_KEY || null;
const amplitudeEngagement = useAmplitudeEngagementInit({
  apiKey: amplitudeApiKey,
  segmentAnalytics: window.analytics,
  store,
});
provide(AMPLITUDE_ENGAGEMENT_PROVIDE_KEY, amplitudeEngagement);

// Setup segment analytics with Amplitude Engagement integration
const segment = SegmentService.forParent(window.analytics, amplitudeEngagement);
provide(SEGMENT_PROVIDE_KEY, segment);
useSegmentPageViews(segment);

// Setup global confirm dialog
provide('globalConfirmDialog', {
  confirm: (...args) => confirmDialog.value?.confirmWithText(...args),
});

// Setup chime functionality
const { chime, chimeText, chimeTime, error, regularChime } = useChime();

// Setup language helper
useLanguageHelper();

// Data
const protocol = window.location.href.includes('https') ? 'https' : 'http';
let baseUrlSuffix = window.PORTAL_BASE_URL_PATH ?? '';
baseUrlSuffix = baseUrlSuffix.startsWith('/') ? baseUrlSuffix : `/${baseUrlSuffix}`;
const baseUrl = `${protocol}://${window.location.host}${baseUrlSuffix}`;

const alertIsVisible = ref(true);
const complaintFormDialog = ref(null);
const confirmDialog = ref(null);
const helpFlyoutRef = ref(null);

const showHelpButton = computed(() => {
  if (!store.state.profile || !store.state.profile.id) return false;
  if (!store.state.config?.enable_help_documentation) return false;
  if (route.meta?.public) return false;
  return true;
});
const sign_out_url = baseUrl;
const transitionName = ref('');

// Computed
const vAppClass = computed(() => [
  router.currentRoute.value.meta.background || 'bg-white',
  { 'fill-height': router.currentRoute.value.meta.fillHeight },
]);

// Watch
watch(
  () => router.currentRoute.value,
  (to, from = { meta: {}, query: {} }) => {
    if (to.meta.depth > from.meta.depth) {
      transitionName.value = 'slide-left';
    } else {
      transitionName.value = 'slide-right';
    }
  },
  { immediate: true },
);

// Functions
async function navigateToHome() {
  if (store.state.config.has_external_welcome_html) {
    window.location.href = '/';
  } else {
    await router.push({ name: 'Landing' });
  }
}

function openComplaintForm() {
  complaintFormDialog.value?.open();
}

async function signOut() {
  setTimeout(() => {
    api.member.session.destroy(() => {
      const signOutUrl = new URL(sign_out_url);
      signOutUrl.searchParams.set('lang', locale.value);
      window.location.href = signOutUrl.href;
    });
  }, 200);
}
</script>

<style scoped>
.wrapper {
  display: flex;
  justify-content: center;
}

.alert-bar {
  padding: 0;
}

.alert-css {
  display: inline-block;
  white-space: pre-wrap;
  margin-top: 0;
  padding-top: 0;
  flex: 1;
}

.alert-wrapper {
  display: flex;
  align-items: center;
  max-width: 100%;
  overflow-y: auto;
  padding: 10px;
  box-sizing: border-box;
}

.icon-wrapper {
  /* flex aligns icon with text across multiple lines of text  */
  display: flex;
  padding-top: 10px;
}

.structured-alert-bar {
  align-items: center;
  height: auto !important;
  min-height: 60px;
  max-height: none;
  overflow: visible;
  padding: 16px;
}

.structured-alert-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.structured-alert-text {
  display: block;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  line-height: 1.35;
}

.structured-alert-text :deep(span),
.structured-alert-text :deep(a) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.structured-alert-close {
  align-self: flex-start;
  flex-shrink: 0;
  margin-top: -2px;
}

.alert-bar .v-icon,
.structured-alert-bar .v-icon {
  opacity: var(--v-medium-emphasis-opacity);
}

@media (max-width: 37.5rem) {
  img {
    padding-left: 0.3125rem;
    margin-left: 0.3125rem;
  }

  .structured-alert-bar {
    height: auto !important;
    min-height: 0;
    padding: 8px;
  }

  .structured-alert-text {
    font-size: 0.875rem;
    line-height: 1.3;
  }
}
</style>
